import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, ClickAwayListener, InputAdornment, Paper, Popper, TextField, Tooltip, Typography,} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {evaluateFormula, getReferencedLevels, isFormula, parseFormula} from '../../../../../../core/services/formula-engine';
import {formatMoney} from '../../../../../../core/services/utils/utils';
import {private_safeAlpha} from "@mui/system";

/**
 * Returns an array of segments (plain-text and token objects) from the formula string.
 * Used to render the syntax-highlighted overlay.
 *
 * @param {string} value
 * @returns {Array<{type: 'text'|'token', content: string, index: number|null}>}
 */
function tokenize(value) {
    if (!value) return [{type: 'text', content: '', index: null}];
    const segments = [];
    const ALL_TOKENS_RE = /\[P(\d+)\]|\[(Cost|Avg\s+Cost)\]/gi;
    let lastIdx = 0;
    for (const match of value.matchAll(ALL_TOKENS_RE)) {
        if (match.index > lastIdx) {
            segments.push({type: 'text', content: value.slice(lastIdx, match.index), index: null});
        }
        if (match[1]) {
            segments.push({type: 'token', content: match[0], index: parseInt(match[1], 10), isSpecial: false});
        } else if (match[2]) {
            const isAvg = /avg/i.test(match[2]);
            segments.push({type: 'token', content: match[0], index: isAvg ? 'Avg Cost' : 'Cost', isSpecial: true});
        }
        lastIdx = match.index + match[0].length;
    }
    if (lastIdx < value.length) {
        segments.push({type: 'text', content: value.slice(lastIdx), index: null});
    }
    return segments;
}

/**
 * A rich formula input that:
 * - Renders an editable input styled identically to OutlinedInput
 * - Shows syntax-highlighted [Pn] tokens (via overlay)
 * - Displays a computed result end-adornment chip
 * - Shows a [Pn] insertion popover when the user types "[" or presses the trigger
 * - Shows inline error messages for syntax errors and circular dependencies
 *
 * @param {{
 *   value: string,
 *   onChange: (val: string) => void,
 *   onBlur: (e: React.FocusEvent) => void,
 *   onKeyDown: (e: React.KeyboardEvent) => void,
 *   priceLevels: Array<{id: number, title: string, levelIndex: number}>,
 *   resolvedValues: Record<number, number|null>,
 *   disabled: boolean,
 *   error: boolean,
 *   circularError: string | null,
 *   label: string,
 *   inputProps: object,
 * }} props
 */
const FormulaInput = ({
                          value = '',
                          onChange,
                          onBlur,
                          onKeyDown,
                          priceLevels = [],
                          resolvedValues = {},
                          specialResolvedValues = {},
                          disabled = false,
                          error = false,
                          circularError = null,
                          label,
                          inputProps: extraInputProps,
                          autoFocus = false,
                      }) => {
    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific']?.chart ?? {}), [theme]);

    const referencedIndices = useMemo(() => getReferencedLevels(value), [value]);

    const inputRef = useRef(null);
    const overlayRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const popperOpen = Boolean(anchorEl);

    /**
     * When the popover opens, reset the selected index to 0 to ensure consistent keyboard navigation starting point.
     */
    useEffect(() => {
        if (popperOpen) setSelectedIndex(0);
    }, [popperOpen]);

    useEffect(() => {
        if (!autoFocus || disabled) return;
        const input = inputRef.current?.querySelector('input');
        if (!input) return;
        const frame = requestAnimationFrame(() => {
            input.focus();
            input.select?.();
        });
        return () => cancelAnimationFrame(frame);
    }, [autoFocus, disabled]);

    // ── Parse formula for UI feedback ────────────────────────────────────────

    const parseResult = useMemo(() => {
        if (!isFormula(value)) return {valid: true};
        const res = parseFormula(value, priceLevels.length);
        if (!res.valid) return {valid: false, error: res.error};
        return {valid: true, error: null};
    }, [value, priceLevels.length]);

    const evalResult = useMemo(() => {
        if (!isFormula(value)) return null;
        // Build resolvedValues map keyed by 1-based index
        const indexedResolved = {};
        priceLevels.forEach((pl, i) => {
            indexedResolved[i + 1] = resolvedValues[pl.id] ?? null;
        });
        return evaluateFormula(value, indexedResolved, specialResolvedValues);
    }, [value, priceLevels, resolvedValues, specialResolvedValues]);

    // ── Token color lookup ────────────────────────────────────────────────────
    /**
     * Returns a consistent color for a given price level index, cycling through theme colors.
     * @type {Function}
     */
    const getTokenColor = useCallback((key) => {
        if (key === 'Cost') return theme.palette?.success?.main || theme.palette.success.main;
        if (key === 'Avg Cost') return theme.palette?.info?.main || theme.palette.info.main;
        return chartColors[(key - 1) % chartColors.length] ?? theme.palette.primary.main;
    }, [chartColors, theme]);

    // ── Segments for overlay ──────────────────────────────────────────────────
    const segments = useMemo(() => tokenize(value), [value]);

    // ── Scroll Synchronization ────────────────────────────────────────────────
    /**
     * Synchronizes the scroll position of the overlay with the native input.
     */
    const syncScroll = useCallback(() => {
        const input = inputRef.current?.querySelector('input');
        const overlay = overlayRef.current;
        if (input && overlay) {
            overlay.scrollLeft = input.scrollLeft;
        }
    }, []);

    /**
     *  Sync scroll on mount and when value changes
     */
    useEffect(() => {
        const input = inputRef.current?.querySelector('input');
        if (input) {
            input.addEventListener('scroll', syncScroll);
            // Initial sync
            syncScroll();
            return () => input.removeEventListener('scroll', syncScroll);
        }
    }, [syncScroll, value]);

    /**
     * Inserts the token for the given string value at the current cursor position,
     * @type {(function(*): void)}
     */
    const insertToken = useCallback((tokenValue) => {
        const el = inputRef.current?.querySelector('input') ?? inputRef.current;
        if (!el) return;
        const cursor = el.selectionStart ?? value.length;
        const token = `[${tokenValue}]`;

        // Remove the "[" that triggered the popover (if present just before cursor)
        const before = value.slice(0, cursor);
        const after = value.slice(cursor);
        const newBefore = before.endsWith('[') ? before.slice(0, -1) : before;
        const newValue = newBefore + token + after;

        onChange && onChange(newValue);
        setAnchorEl(null);

        // Restore caret position after state update
        setTimeout(() => {
            const newCursor = newBefore.length + token.length;
            el.setSelectionRange(newCursor, newCursor);
            el.focus();
        }, 0);
    }, [onChange, value]);

    const popoverItems = useMemo(() => {
        const items = priceLevels.map((pl, i) => ({
            id: pl.id,
            tokenValue: `P${i + 1}`,
            label: `[P${i + 1}]`,
            title: pl.title,
            value: resolvedValues[pl.id],
            color: getTokenColor(i + 1),
            isUsed: referencedIndices.has(i + 1),
        }));

        // Add Cost and Avg Cost
        items.unshift(
            {
                id: 'cost',
                tokenValue: 'Cost',
                label: '[Cost]',
                title: 'Current Cost',
                value: specialResolvedValues['Cost'],
                color: theme.palette.success.main,
                isUsed: value.toLowerCase().includes('[cost]'),
            },
            {
                id: 'avgCost',
                tokenValue: 'Avg Cost',
                label: '[Avg Cost]',
                title: 'Average Cost',
                value: specialResolvedValues['Avg Cost'],
                color: theme.palette.info.main,
                isUsed: value.toLowerCase().includes('[avg cost]'),
            }
        );
        return items;
    }, [priceLevels, resolvedValues, specialResolvedValues, theme.palette.success?.main, theme.palette.info?.main, value, getTokenColor, referencedIndices]);

    // ── Popover: detect "[" and offer insertion ────────────────────────────────
    /**
     * Handles input changes to update the value and control popover visibility when "[" is typed.
     * @type {(function(*): void)|*}
     */
    const handleKeyDown = useCallback((e) => {
        if (popperOpen && popoverItems.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((prev) => (prev + 1) % popoverItems.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((prev) => (prev - 1 + popoverItems.length) % popoverItems.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                insertToken(popoverItems[selectedIndex].tokenValue);
            } else if (e.key === 'Escape') {
                setAnchorEl(null);
            }
        }
        onKeyDown && onKeyDown(e);
    }, [popperOpen, popoverItems, selectedIndex, insertToken, onKeyDown]);

    const handleBlur = useCallback((e) => {
        if (popperOpen) return;
        onBlur?.(e);
    }, [popperOpen, onBlur]);

    /**
     * Handles input changes to update the value and control popover visibility when "[" is typed.
     * @type {(function(*): void)|*}
     */
    const handleInputChange = useCallback((e) => {
        const newValue = e.target.value;
        onChange && onChange(newValue);

        // Sync scroll after change
        requestAnimationFrame(syncScroll);

        // Open popover when "[" is the last typed char
        const cursor = e.target.selectionStart;
        if (newValue[cursor - 1] === '[') {
            setAnchorEl(e.currentTarget);
        } else {
            setAnchorEl(null);
        }
    }, [onChange, syncScroll]);

    // ── End adornment: computed value chip ────────────────────────────────────

    const endAdornment = useMemo(() => {
        if (!isFormula(value)) return null;
        const hasCircularError = !!circularError && !anchorEl; // Suppress while editing
        const hasSyntaxError = parseResult.valid === false && !!parseResult.error;
        const hasEvalError = !!evalResult?.error;

        const adornmentSx = {zIndex: 10, position: 'relative'};

        if (hasCircularError) {
            return (
                <InputAdornment position="end" sx={adornmentSx}>
                    <Tooltip title={circularError} placement="top">
                        <Box sx={chipSx('error', theme)}>⊗ Circular</Box>
                    </Tooltip>
                </InputAdornment>
            );
        }
        if (hasSyntaxError) {
            return (
                <InputAdornment position="end" sx={adornmentSx}>
                    <Tooltip title={parseResult.error} placement="top">
                        <Box sx={chipSx('warning', theme)}>⚠ Syntax</Box>
                    </Tooltip>
                </InputAdornment>
            );
        }
        if (hasEvalError) {
            return (
                <InputAdornment position="end" sx={adornmentSx}>
                    <Tooltip title={evalResult.error} placement="top">
                        <Box sx={chipSx('warning', theme)}>⚠ Error</Box>
                    </Tooltip>
                </InputAdornment>
            );
        }
        if (evalResult?.result !== null && evalResult?.result !== undefined) {
            return (
                <InputAdornment position="end" sx={adornmentSx}>
                    <Box sx={chipSx('success', theme)}>
                        {`= ${formatMoney(evalResult.result)}`}
                    </Box>
                </InputAdornment>
            );
        }
        return null;
    }, [value, circularError, anchorEl, parseResult.valid, parseResult.error, evalResult?.error, evalResult?.result, theme]);

    // ── Highlight overlay ─────────────────────────────────────────────────────
    // Rendered as an absolutely-positioned overlay on the input text.
    // The real <input> sits on top (color: transparent text, caret visible).
    const highlightOverlay = useMemo(() => {
        if (!isFormula(value)) return null;
        return (
            <Box
                ref={overlayRef}
                aria-hidden="true"
                className={'formula-input-overlay'}
                sx={{
                    borderRadius: 1,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    paddingInlineStart: '14px',
                    paddingInlineEnd: endAdornment ? '110px' : '14px',
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    whiteSpace: 'pre',
                    fontFamily: 'inherit',
                    lineHeight: 'inherit',
                    letterSpacing: 'inherit',
                    fontSize: '14px',
                    // Compensate for any slight misalignment
                    transform: 'translateY(-1px)',
                }}
            >
                {
                    segments.map((seg, i) =>
                        seg.type === 'text'
                            ? <span key={i} style={{color: theme.palette.text.primary}}>{seg.content}</span>
                            : (
                                <Tooltip
                                    key={i}
                                    title={
                                        seg.isSpecial
                                            ? `${seg.index === 'Cost' ? 'Current Cost' : 'Average Cost'}: ${formatMoney(specialResolvedValues[seg.index] ?? 0)}`
                                            : `${priceLevels[seg.index - 1]?.title ?? `P${seg.index}`}: ${resolvedValues[priceLevels[seg.index - 1]?.id] != null ? formatMoney(resolvedValues[priceLevels[seg.index - 1]?.id]) : 'N/A'}`
                                    }
                                    placement="top"
                                    arrow
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            color: getTokenColor(seg.index),
                                            textShadow: `0 0 1px ${private_safeAlpha(getTokenColor(seg.index), 1)}`,
                                            boxShadow: `0 0 0 2px ${private_safeAlpha(getTokenColor(seg.index), 0.22)}`,
                                            backgroundColor: `${private_safeAlpha(getTokenColor(seg.index), 0.22)}`,
                                            borderRadius: '2px',
                                            cursor: 'default',
                                            pointerEvents: 'auto',
                                        }}
                                    >
                                        {seg.content}
                                    </Box>
                                </Tooltip>
                            )
                    )}
            </Box>
        );
    }, [value, endAdornment, segments, theme.palette.text.primary, specialResolvedValues, priceLevels, resolvedValues, getTokenColor]);

    return (
        <>
            {/* Input wrapper */}
            <Box ref={inputRef} sx={{position: 'relative', width: '100%'}}>
                <TextField
                    variant="outlined"
                    fullWidth
                    label={label}
                    value={value}
                    disabled={disabled}
                    error={error || !!circularError || (isFormula(value) && !parseResult.valid)}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    inputProps={{
                        ...extraInputProps,
                        style: {
                            // Make the native text transparent when formula mode so the overlay shows through
                            color: isFormula(value) ? 'transparent' : 'inherit',
                            caretColor: theme.palette.text.primary,
                        },
                    }}
                    InputProps={{
                        endAdornment,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            position: 'relative',
                        },
                    }}
                />
                {highlightOverlay}
            </Box>

            {/* insertion popover */}
            <Popper
                open={popperOpen && priceLevels.length > 0 && !disabled}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{zIndex: 1400}}
            >
                <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                    <Paper
                        elevation={6}
                        sx={{
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            maxHeight: 240,
                            overflowY: 'auto',
                            minWidth: 220,
                        }}
                    >
                        <Typography variant="body1" fontWeight={600} color="text.primary" sx={{px: 1, pb: 1}}>
                            Insert dynamic reference
                        </Typography>
                        {
                            popoverItems.map((item, i) => {
                                return (
                                    <Box
                                        key={item.id}
                                        onMouseDown={(e) => {
                                            e.preventDefault(); // Prevent input blur
                                            insertToken(item.tokenValue);
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 1,
                                            py: 0.75,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            backgroundColor: (selectedIndex === i) ? 'action.hover' : (item.isUsed ? 'action.selected' : 'transparent'),
                                            border: (selectedIndex === i) ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                                            '&:hover': {backgroundColor: 'action.hover'},
                                        }}
                                    >
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color, flexShrink: 0}}/>
                                        <Typography variant="body2" fontWeight={600} color={item.color} sx={{fontFamily: 'monospace'}}>
                                            {item.label}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{flexGrow: 1, textAlign: 'right'}}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {item.value != null ? formatMoney(item.value) : '—'}
                                        </Typography>
                                    </Box>
                                );
                            })
                        }
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
};

/**
 * Styles for the end-adornment result chip based on variant.
 */
function chipSx(variant, theme) {
    const map = {
        success: {bg: theme.palette.success.main, color: theme.palette.success.contrastText},
        warning: {bg: theme.palette.warning.main, color: theme.palette.warning.contrastText},
        error: {bg: theme.palette.error.main, color: theme.palette.error.contrastText},
    };
    const {bg, color} = map[variant] ?? map.success;
    return {
        backgroundColor: bg,
        color,
        fontSize: '0.7rem',
        fontWeight: 600,
        px: 1,
        py: 0.25,
        borderRadius: 1,
        whiteSpace: 'nowrap',
        maxWidth: 120,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: variant !== 'success' ? 'help' : 'default',
    };
}

export default FormulaInput;
